import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldCheck } from 'lucide-react';
import { verifyMfaCode, mfaErrorMessage } from '@/lib/adminMfa';

/**
 * Second-factor prompt after a correct password. Accepts either a 6-digit TOTP
 * code or one of the one-time recovery codes.
 */
const AdminMfaChallenge = ({ onVerified, onCancel }) => {
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Enter the code from your authenticator.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await verifyMfaCode(trimmed, rememberDevice);
      onVerified?.(result);
    } catch (err) {
      setError(mfaErrorMessage(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Card className="border-gray-700 bg-gray-900/80 text-white backdrop-blur-sm">
          <CardHeader className="space-y-4 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-red-500 to-orange-500">
              <ShieldCheck className="h-7 w-7 text-white" />
            </div>
            <div>
              <CardTitle className="text-xl font-bold text-white">Two-factor verification</CardTitle>
              <CardDescription className="text-gray-400">
                Enter the 6-digit code from your authenticator.
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                value={code}
                onChange={(e) => { setCode(e.target.value); setError(''); }}
                placeholder="123456"
                inputMode="text"
                autoComplete="one-time-code"
                autoFocus
                className="bg-gray-800/50 text-center text-2xl tracking-[0.3em] text-white"
                disabled={busy}
              />

              {error && <p className="text-sm text-red-400">{error}</p>}

              <label className="flex items-start gap-2 text-sm text-gray-300">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  disabled={busy}
                />
                Trust this device for 30 days
              </label>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-red-500 to-orange-500 font-semibold"
                disabled={busy}
              >
                {busy ? 'Verifying…' : 'Verify and continue'}
              </Button>

              <div className="space-y-3 border-t border-gray-700 pt-4">
                <p className="text-xs text-gray-500">
                  Lost your authenticator? Enter one of your recovery codes above instead.
                </p>
                <button
                  type="button"
                  onClick={onCancel}
                  className="text-xs text-gray-400 underline hover:text-gray-200"
                  disabled={busy}
                >
                  Sign out
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default AdminMfaChallenge;
